import type { PlacedTower, ActiveUnit, Element } from '../interfaces/index.js'

export interface AttackResult {
  damage: number
  rawDamage: number
  isCrit: boolean
  isDodge: boolean
  elementMultiplier: number
  upgradeMultiplier: number
}

export class CombatSystem {
  private elementMatrix: Record<string, Record<string, number>>
  private upgradePerLevelBonus: number

  constructor(
    elementMatrix: Record<string, Record<string, number>>,
    upgradePerLevelBonus: number = 0.18
  ) {
    this.elementMatrix = elementMatrix
    this.upgradePerLevelBonus = upgradePerLevelBonus
  }

  tryAttack(tower: PlacedTower, unit: ActiveUnit, nowMs: number): AttackResult | null {
    const critRoll = Math.random()
    const dodgeRoll = Math.random()
    return this.tryAttackWithRolls(tower, unit, nowMs, critRoll, dodgeRoll)
  }

  tryAttackWithRolls(
    tower: PlacedTower,
    unit: ActiveUnit,
    nowMs: number,
    critRoll: number,
    dodgeRoll: number
  ): AttackResult | null {
    const cooldown = 1000 / tower.spd
    if (nowMs - tower.lastShot < cooldown) {
      return null
    }

    const isCrit = critRoll < tower.crit
    const isDodge = dodgeRoll < unit.dodge
    const upgradeMultiplier = this.getUpgradeDamageMultiplier(tower.level)
    const rawDamage = tower.dmg * upgradeMultiplier * (isCrit ? 1.6 : 1.0)
    const elementMultiplier = this.getElementMultiplier(tower.el, unit.el)
    const finalDamage = isDodge ? 0 : this.applyDamage(unit, rawDamage, elementMultiplier, unit.armor)

    tower.lastShot = nowMs

    return {
      damage: finalDamage,
      rawDamage,
      isCrit,
      isDodge,
      elementMultiplier,
      upgradeMultiplier,
    }
  }

  applyDamage(unit: ActiveUnit, rawDamage: number, elementMultiplier: number, armor: number): number {
    const damage = Math.floor(rawDamage * elementMultiplier * (1 - armor))
    unit.curHp -= damage
    unit.alive = unit.curHp > 0
    return damage
  }

  getElementMultiplier(towerEl: Element, unitEl: Element): number {
    if (towerEl === 'neutral' || unitEl === 'neutral') {
      return 1.0
    }
    const row = this.elementMatrix[towerEl]
    if (row === undefined) {
      return 1.0
    }
    const multiplier = row[unitEl]
    if (multiplier === undefined) {
      return 1.0
    }
    return multiplier
  }

  getUpgradeDamageMultiplier(level: number): number {
    return 1.0 + (level - 1) * this.upgradePerLevelBonus
  }

  getUnitsInRange(tower: PlacedTower, units: ActiveUnit[]): ActiveUnit[] {
    // Use squared range to avoid Math.sqrt — equivalent comparison because both sides ≥ 0.
    const rangeSq = tower.range * tower.range
    return units.filter((unit) => {
      if (!unit.alive) return false
      const dx = unit.gx - tower.gx
      const dy = unit.gy - tower.gy
      return dx * dx + dy * dy <= rangeSq
    })
  }

  getChainTargets(
    primary: ActiveUnit,
    allUnits: ActiveUnit[],
    chainCount: number,
    chainRange: number
  ): ActiveUnit[] {
    const chainRangeSq = chainRange * chainRange
    const candidates = allUnits.filter((unit) => {
      if (!unit.alive) return false
      if (unit === primary) return false
      const dx = unit.gx - primary.gx
      const dy = unit.gy - primary.gy
      return dx * dx + dy * dy <= chainRangeSq
    })

    // Sort by squared distance — preserves order, no sqrt needed.
    candidates.sort((a, b) => {
      const dxA = a.gx - primary.gx
      const dyA = a.gy - primary.gy
      const dxB = b.gx - primary.gx
      const dyB = b.gy - primary.gy
      return (dxA * dxA + dyA * dyA) - (dxB * dxB + dyB * dyB)
    })

    return candidates.slice(0, chainCount)
  }
}
